import { css } from '@emotion/react';
import styled from '@emotion/styled';
import React from 'react';
import { Box } from './box';
import { Button } from './buttons';
import SearchIcon from '~Assets/icons/search.svg';
import CloseIcon from '~Assets/icons/close.svg';

export const BaseInputStyling = css`
  background-color: var(--colorBlueGray50);
  min-height: 42px;
  border-radius: 4px;
  width: 100%;
  box-sizing: border-box;
  font-family: 'Eina01', 'HelveticaNeue', Helvetica, Arial, sans-serif;
  border: 1px solid var(--colorBlueGray200);
  box-sizing: border-box;
  border-radius: 4px;
  transition: box-shadow 0.2s ease, border-color 0.2s ease;

  &::placeholder {
    color: var(--colorBlueGray500);
  }

  &:focus {
    outline: 0;
    // Box shadow outline to avoid position shifting
    box-shadow: 0 0 0 1px #444;
  }
`;

export const BaseDarkInputStyling = css`
  ${BaseInputStyling}

  background-color: var(--colorBlueGray1200);
  border: 1px solid var(--colorBlueGray900);

  color: var(--colorWhite);

  &::placeholder {
    color: var(--colorBlueGray200);
  }

  &:focus {
    outline: 0;
    // Box shadow outline to avoid position shifting
    box-shadow: 0 0 0 1px #444;
  }
`;

type InputProps = React.InputHTMLAttributes<HTMLInputElement> & {
  error?: boolean;
  uppercase?: boolean;
  dark?: boolean;
  isSearch?: boolean;
  autoFocus?: boolean;
};

/* .... SEARCH INPUT WRAPPER .... */
const SearchInputWrapperStyled = styled(Box)`
  position: relative;
  display: flex;
  align-items: center;
  width: 100%;

  &::before {
    content: '';
    position: absolute;
    left: 12px;
    top: 50%;
    transform: translateY(-50%);
    width: 20px;
    height: 20px;
    background-color: var(--colorBlueGray900);
    -webkit-mask-image: url(${SearchIcon});
    mask-image: url(${SearchIcon});
    -webkit-mask-size: contain;
    mask-size: contain;
    -webkit-mask-repeat: no-repeat;
    mask-repeat: no-repeat;
    -webkit-mask-position: center;
    mask-position: center;
    pointer-events: none;
    z-index: 1;
  }
`;

/* .... CLEAR BUTTON .... */
const ClearButton = styled.button`
  position: absolute;
  right: 12px;
  top: 50%;
  transform: translateY(-50%);
  width: 20px;
  height: 20px;
  background-color: var(--colorBlueGray900);
  -webkit-mask-image: url(${CloseIcon});
  mask-image: url(${CloseIcon});
  -webkit-mask-size: contain;
  mask-size: contain;
  -webkit-mask-repeat: no-repeat;
  mask-repeat: no-repeat;
  -webkit-mask-position: center;
  mask-position: center;
  border: none;
  padding: 0;
  cursor: pointer;
  z-index: 1;
  transition: background-color 0.2s ease;

  &:hover {
    background-color: var(--colorBlueGray1000);
  }

  &:active {
    background-color: var(--colorBlack);
  }
`;

/* .... SEARCH INPUT WRAPPER WITH CLEAR FUNCTIONALITY .... */
interface SearchInputWrapperProps {
  children: React.ReactNode;
  value?: string;
  onClear?: () => void;
  className?: string;
  fluid?: boolean;
}

export const SearchInputWrapper: React.FC<SearchInputWrapperProps> = ({
  children,
  value,
  onClear,
  className,
  fluid,
}) => {
  const showClearButton = value && value.length > 0 && onClear;

  return React.createElement(
    SearchInputWrapperStyled,
    { className, fluid },
    children,
    showClearButton &&
      React.createElement(ClearButton, { onClick: onClear, type: 'button' as const }),
  );
};

/* .... INPUT .... */
export const Input = styled(Box)<InputProps>`
  ${({ dark }) =>
    dark
      ? css`
          border: 1px solid var(--colorBlueGray1000);
          background-color: var(--colorBlueGray1100);
          color: var(--colorWhite);
          &:focus {
            border-color: var(--colorBlueGray900);
            outline: none;
          }
        `
      : BaseInputStyling}

  ${({ isSearch }) =>
    isSearch &&
    css`
      padding-left: 40px;
      padding-right: 40px;
    `}

  ${({ uppercase }) =>
    uppercase && 'text-transform:uppercase; ::placeholder{text-transform: none}'}

  font-family: 'Eina01', 'HelveticaNeue', Helvetica, Arial, sans-serif;
  border-radius: 4px;
  border: 1px solid
    ${({ error, dark }) =>
      error ? 'var(--colorUIRed)' : dark ? 'var(--colorBlueGray1000)' : 'var(--colorBlueGray200)'};
`;

Input.defaultProps = {
  as: 'input',
  padding: 'small',
};

/* .... TEXTAREA .... */
type TextareaProps = Omit<React.TextareaHTMLAttributes<HTMLTextAreaElement>, 'onChange'> & {
  error?: boolean;
  uppercase?: boolean;
  dark?: boolean;
  onChange?: (event: React.ChangeEvent<HTMLTextAreaElement>) => void;
};

const TextareaBase = styled(Box)<TextareaProps>`
  ${({ dark }) =>
    dark
      ? css`
          border: 1px solid var(--colorBlueGray1000);
          background-color: var(--colorBlueGray1100);
          color: var(--colorWhite);
          &:focus {
            border-color: var(--colorBlueGray900);
            outline: none;
          }
        `
      : BaseInputStyling}

  ${({ uppercase }) =>
    uppercase &&
    'text-transform:uppercase; ::placeholder{text-transform: none}'}

  font-family: 'Eina01', 'HelveticaNeue', Helvetica, Arial, sans-serif;
  border-radius: 4px;
  border: 1px solid
    ${({ error, dark }) =>
      error ? 'var(--colorUIRed)' : dark ? 'var(--colorBlueGray1000)' : 'var(--colorBlueGray200)'};
  resize: vertical;
  min-height: 80px;
`;

TextareaBase.defaultProps = {
  as: 'textarea',
  padding: 'small',
};

export const Textarea = TextareaBase as React.ComponentType<TextareaProps>;

/* .... SELECT .... */
export const Select = styled(Box)<{
  type?: string;
  placeholder?: string;
  name?: string;
  value?: string | number;
  onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  error?: boolean;
}>`
  appearance: none;
  ${BaseInputStyling}
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' height='24px' viewBox='0 0 24 24' width='24px' fill='%23000000'%3E%3Cpath d='M0 0h24v24H0z' fill='none'/%3E%3Cpath d='M12 5.83L15.17 9l1.41-1.41L12 3 7.41 7.59 8.83 9 12 5.83zm0 12.34L8.83 15l-1.41 1.41L12 21l4.59-4.59L15.17 15 12 18.17z'/%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: right 1em bottom 12px;
  background-size: 16px;
  border: 1px solid ${({ error }) => (error ? 'var(--colorUIRed)' : 'var(--colorBlueGray200)')};
`;

Select.defaultProps = {
  as: 'select',
  padding: 'small',
};

/* .... SELECTABLE BUTTON .... */
export const SelectableButton = styled(Button)<{
  type?: string;
  name?: string;
  disabled?: boolean;
  value: string | number;
  label: string;
}>`
  // Hide the default radio button 
  appearance: none;
  position: relative;
  width: auto;

  // Create a custom radio button
  &:after {
    display: flex;
    height: 100%;
    width: 100%;
    line-height: 36px;
    font-size: 16px;
    box-sizing: border-box;
    padding: 0 16px;
    content: '${(props) => `${props.label}`}';
    justify-content: center;
    align-items: center;
  }

  // Checked state
  &:not(:checked) {
    border: 2px solid transparent;
  }
  &:checked {
    border: 2px solid var(--colorBlack);
  }
`;

SelectableButton.defaultProps = {
  as: 'input',
  type: 'radio',
  backgroundColor: 'colorBlueGray50',
};
